import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {PromptLibraryApi} from '../../../api/promptLibraryApi';
import {Switch} from '../../buttons/Swtich';

const ROLES = ['media_buyer', 'analista', 'account', 'director'];
const MOMENTOS = ['dia_a_dia', 'semanal', 'antes_reunion', 'cierre_mes', 'cuando_cambio', 'cliente_nuevo'];

const EMPTY_FORM = {
    title: '', promptText: '', valuePreview: '', role: '', momento: '',
    scope: 'AGENCY', portalVisible: false, changeDescription: '',
};

/**
 * El estado inicial del form. En alta arranca vacío; en edición se precarga con el prompt.
 *
 * Se calcula UNA vez, en el `useState` inicial, porque la sección monta el modal recién cuando se
 * abre y lo keyea por prompt (`key="edit-<id>"`). Sin ese montaje diferido haría falta un efecto
 * que re-hidrate en cada apertura — y ese efecto pisaría lo que el usuario escribió si la lista se
 * refresca con el modal abierto.
 */
export function formFromPrompt(prompt) {
    if (!prompt) return {...EMPTY_FORM};
    return {
        title: prompt.title || '',
        promptText: prompt.prompt_text || '',
        valuePreview: prompt.value_preview || '',
        role: prompt.role || '',
        // El form maneja UN momento y el backend guarda una lista. Al editar se toma el primero:
        // el PUT es parcial, pero `momentos` viaja entero, así que un prompt con dos momentos
        // pierde el segundo si se guarda desde acá (lo mismo que ya pasaba al forkearlo).
        momento: (prompt.momentos || [])[0] || '',
        scope: prompt.scope || 'AGENCY',
        portalVisible: Boolean(prompt.portal_visible),
        changeDescription: '',
    };
}

/**
 * Alta y edición de un prompt de la biblioteca, en un solo modal.
 *
 * `prompt` opcional = modo edición: precarga los campos, cambia título/CTA y hace `PUT` en vez de
 * `POST`. Dos campos se comportan distinto según el modo, y no por prolijidad:
 *  - **Alcance**: el PUT del backend no acepta `scope`, así que en edición se muestra como dato,
 *    no como control. Un radio que no puede guardar nada es peor que no tenerlo.
 *  - **Visible en el portal**: va por un endpoint aparte (`portal-visibility`) y en edición el
 *    switch ya está en la card, a un click. Duplicarlo acá obligaría a encadenar un segundo
 *    request con su propio modo de falla, para algo que ya se resuelve en la pantalla.
 */
export function CreatePromptModal({isOpen, toggle, clientId, prompt = null, onCreated, onUpdated}) {
    const {t} = useTranslation();
    const isEdit = Boolean(prompt);
    const [form, setForm] = useState(() => formFromPrompt(prompt));
    const [saving, setSaving] = useState(false);

    const setField = (field, value) => setForm((prev) => ({...prev, [field]: value}));

    const handleClose = () => {
        if (saving) return;
        toggle();
    };

    // El fallo deja el modal ABIERTO con el botón habilitado: lo que el usuario escribió sigue ahí
    // y puede reintentar. Sin esto, una promesa rechazada lo clavaba en "Guardando...".
    const failWith = (messageKey) => {
        setSaving(false);
        toast.error(t(messageKey));
    };

    const handleUpdate = () => {
        setSaving(true);
        const payload = {
            title: form.title.trim(),
            prompt_text: form.promptText.trim(),
            value_preview: form.valuePreview.trim(),
            role: form.role,
            momentos: form.momento ? [form.momento] : [],
        };
        // Nota para el historial de versiones: sólo viaja si el usuario escribió algo (el PUT no
        // toca lo que no se manda).
        if (form.changeDescription.trim()) payload.change_description = form.changeDescription.trim();

        PromptLibraryApi.update(prompt.id, payload).then((data) => {
            if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR') {
                failWith('prompt_library.edit_error');
                return;
            }
            setSaving(false);
            onUpdated(data);
        }).catch(() => failWith('prompt_library.edit_error'));
    };

    const handleCreate = () => {
        setSaving(true);
        const payload = {
            title: form.title.trim(),
            prompt_text: form.promptText.trim(),
            value_preview: form.valuePreview.trim(),
            role: form.role,
            momentos: form.momento ? [form.momento] : [],
            scope: form.scope,
        };
        if (form.scope === 'CLIENT') {
            payload.agency_client_id = clientId;
        }
        new APIClient(`${API_URL}agent/prompt-library/create/`).call('POST', payload).then((data) => {
            if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR') {
                failWith('prompt_library.create_error');
                return;
            }
            // GAP de contrato: el create no acepta `portal_visible`, así que la publicación al
            // portal se encadena como segundo request. Si ese segundo paso falla, el prompt YA
            // existe: se avisa y se puede prender desde la card.
            if (form.portalVisible && data.id) {
                PromptLibraryApi.setPortalVisibility(data.id, true).then((visRes) => {
                    setSaving(false);
                    if (!visRes || visRes.status === 'ERROR' || visRes.status === 'NETWORK_ERROR') {
                        toast.warning(t('prompt_library.portal_after_create_error'));
                    }
                    onCreated();
                }).catch(() => {
                    // El prompt ya está creado: cerrar es lo correcto. Sin este catch, una promesa
                    // rechazada dejaba el modal clavado en "Guardando..." con el prompt YA en la
                    // biblioteca — y el único camino de salida era recargar la página.
                    setSaving(false);
                    toast.warning(t('prompt_library.portal_after_create_error'));
                    onCreated();
                });
                return;
            }
            setSaving(false);
            onCreated();
        }).catch(() => failWith('prompt_library.create_error'));
    };

    const handleSubmit = () => {
        if (saving) return;
        if (!form.title.trim() || !form.promptText.trim()) {
            toast.error(t('prompt_library.create_validation'));
            return;
        }
        if (isEdit) handleUpdate();
        else handleCreate();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose}>
            <ModalHeader toggle={handleClose}>
                {t(isEdit ? 'prompt_library.edit_title' : 'prompt_library.create_title')}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <div className="form-row">
                        <label htmlFor="pl-field-title">{t('prompt_library.field_title')}</label>
                        <input id="pl-field-title" className="form-control" value={form.title}
                               onChange={(e) => setField('title', e.target.value)}
                               placeholder={t('prompt_library.field_title_ph')}/>
                    </div>
                    <div className="form-row">
                        <label htmlFor="pl-field-value">{t('prompt_library.field_value')}</label>
                        <input id="pl-field-value" className="form-control" value={form.valuePreview}
                               onChange={(e) => setField('valuePreview', e.target.value)}
                               placeholder={t('prompt_library.field_value_ph')}/>
                    </div>
                    <div className="form-row">
                        <label htmlFor="pl-field-prompt">{t('prompt_library.field_prompt')}</label>
                        {/* pl-textarea: sin esto el height fijo de .form-control (2.3em) aplasta
                            el textarea a una línea — es el campo más importante del modal. */}
                        <textarea id="pl-field-prompt" className="form-control pl-textarea" rows={6}
                                  value={form.promptText}
                                  onChange={(e) => setField('promptText', e.target.value)}
                                  placeholder={t('prompt_library.field_prompt_ph')}/>
                    </div>
                    <div className="form-grid row-col-2">
                        <div className="form-row">
                            <label htmlFor="pl-field-role">{t('prompt_library.field_role')}</label>
                            <select id="pl-field-role" className="form-control" value={form.role}
                                    onChange={(e) => setField('role', e.target.value)}>
                                <option value="">{t('prompt_library.field_role_none')}</option>
                                {ROLES.map((r) => <option key={r} value={r}>{t(`prompt_library.roles.${r}`)}</option>)}
                            </select>
                        </div>
                        <div className="form-row">
                            <label htmlFor="pl-field-momento">{t('prompt_library.field_momento')}</label>
                            <select id="pl-field-momento" className="form-control" value={form.momento}
                                    onChange={(e) => setField('momento', e.target.value)}>
                                <option value="">{t('prompt_library.field_momento_none')}</option>
                                {MOMENTOS.map((m) => <option key={m} value={m}>{t(`prompt_library.momentos.${m}`)}</option>)}
                            </select>
                        </div>
                    </div>
                    {isEdit ? (
                        <>
                            <div className="form-row">
                                <label>{t('prompt_library.field_scope')}</label>
                                <p className="pl-scope-locked">
                                    {t(`prompt_library.scope.${form.scope}`)}
                                    {' — '}
                                    {t('prompt_library.field_scope_locked_hint')}
                                </p>
                            </div>
                            <div className="form-row">
                                <label htmlFor="pl-field-change">{t('prompt_library.field_change_description')}</label>
                                <input id="pl-field-change" className="form-control" value={form.changeDescription}
                                       onChange={(e) => setField('changeDescription', e.target.value)}
                                       placeholder={t('prompt_library.field_change_description_ph')}/>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="form-row">
                                <label>{t('prompt_library.field_scope')}</label>
                                <div className="pl-scope-choice">
                                    <label className={form.scope === 'AGENCY' ? 'is-on' : ''}>
                                        <input type="radio" name="pl-scope" checked={form.scope === 'AGENCY'}
                                               onChange={() => setField('scope', 'AGENCY')}/>
                                        {t('prompt_library.scope_agency_option')}
                                    </label>
                                    <label className={form.scope === 'CLIENT' ? 'is-on' : ''}>
                                        <input type="radio" name="pl-scope" checked={form.scope === 'CLIENT'}
                                               onChange={() => setField('scope', 'CLIENT')}/>
                                        {t('prompt_library.scope_client_option')}
                                    </label>
                                </div>
                            </div>
                            <div className="form-row switch-input">
                                <div className="label-with-explanation">
                                    <label>{t('prompt_library.create_portal_visible')}</label>
                                    <p>{t('prompt_library.create_portal_visible_hint')}</p>
                                </div>
                                <Switch
                                    name="pl-create-portal-visible"
                                    checked={form.portalVisible}
                                    onChange={(e) => setField('portalVisible', e.target.checked)}
                                    style={{margin: 0}}
                                />
                            </div>
                        </>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -negative" onClick={handleClose}>{t('prompt_library.cancel')}</button>
                <button type="button" className="btn -alt" onClick={handleSubmit} disabled={saving}>
                    {saving
                        ? t(isEdit ? 'prompt_library.edit_saving' : 'prompt_library.saving')
                        : t(isEdit ? 'prompt_library.edit_save' : 'prompt_library.save')}
                </button>
            </ModalFooter>
        </Modal>
    );
}
